<template>
  <RemotePaginatedList
    :filter-options="filterOptions"
    :headers="headers"
    multi-sort
    v-model="selectedItemIds"
    data-url="admin/contract/admin/"
    :extra-params="{ contract_id: contractId }"
    :defaultTableOptions="{ itemsPerPage: -1 }"
    data-key="admins"
    :refresh-trigger.sync="innerRefreshTrigger"
    v-bind="$attrs"
    :total-item-count.sync="totalItemCount"
    hide-default-footer
    class="contract-admin-list"
  >
    <template #item.status="{ value }">
      <span :class="['admin-status-span', value]">
        {{ value }}
        <div class="admin-status-dot"></div>
      </span>
    </template>
    <template #item.updated_at="{ value }">
      {{ $parseDate(value) }}
    </template>
    <template v-slot:item.actions="{ item }">
      <div class="d-flex">
        <v-btn
          text
          color="primary"
          v-if="item.status === 'PENDING' || item.status === 'DECLINED'"
          @click="approveItem(item)"
        >
          APPROVE
        </v-btn>
        <!--        <v-btn text-->
        <!--               color="primary"-->
        <!--               v-if="item.status === 'PENDING' || item.status === 'APPROVED'"-->
        <!--               @click="declineItem(item)">-->
        <!--          DECLINE-->
        <!--        </v-btn>-->
        <v-btn
          text
          color="primary"
          v-if="
            item.managed_work_orders_count === 0 &&
            totalItemCount > 1 &&
            item.status === 'APPROVED' &&
            canTransferItem(item)
          "
          @click="transferItem(item)"
        >
          TRANSFER
        </v-btn>
        <v-btn
          text
          color="primary"
          v-if="
            item.managed_work_orders_count === 0 &&
            totalItemCount > 1 &&
            canDeleteItem(item)
          "
          @click="deleteItem(item)"
        >
          DELETE
        </v-btn>
      </div>
    </template>
  </RemotePaginatedList>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import RemotePaginatedList from "../RemotePaginatedList";
import _ from "lodash";
import { ACTION_TYPES } from "@/store/types";
import SelectAccountDialog from "@/components/SelectAccountDialog.vue";
import { USER_TYPE } from "@/constants";

export default {
  name: "ContractAdminList",
  components: { RemotePaginatedList },
  props: {
    value: Array,
    refreshTrigger: Boolean,

    contractId: String,
  },
  setup(props, { root, emit }) {
    const me = computed(() => root.$me());
    const refresh = function () {
      innerRefreshTrigger.value = true;
    };
    onMounted(() => {
      refresh(false);
    });

    watch(
      () => props.refreshTrigger,
      (newValue) => {
        if (newValue) {
          refresh(true);
          emit("update:refresh-trigger", false);
        }
      }
    );

    // Filter
    const filterOptions = ref([]);

    // Data Table
    const headers = ref([
      { text: root.$t("contract_admin.status"), value: "status", width: 150 },
      {
        text: root.$t("contract_admin.name"),
        value: "account.name",
        width: 200,
      },
      { text: root.$t("contract_admin.post"), value: "account.post" },
      { text: root.$t("contract_admin.company"), value: "account.company" },
      {
        text: root.$t("contract_admin.managed_work_orders_count"),
        value: "managed_work_orders_count",
      },
      { text: "", value: "actions", sortable: false, width: 250 },
    ]);
    const innerRefreshTrigger = ref(false);

    const selectedItemIds = ref([]);
    const totalItemCount = ref(0);
    watch(
      () => selectedItemIds.value,
      (newValue) => {
        emit("input", newValue);
      }
    );
    watch(
      () => props.value,
      (newValue, oldValue) => {
        if (_.xor(newValue, oldValue).length) {
          selectedItemIds.value = newValue;
        }
      },
      { deep: true, immediate: true }
    );

    const editItem = async function (item) {
      root.$router.push({ name: "sor-detail", params: { sorId: item.id } });
    };
    const canDeleteItem = function (item) {
      return (
        ([USER_TYPE.ADMIN, USER_TYPE.SUPER_ADMIN].indexOf(me.value.type) !==
          -1 ||
          me.value.managed_contract_ids.indexOf(props.contractId) !== -1) &&
        item.account.id !== me.value.id
      );
    };

    const deleteItem = async function (item) {
      if (
        await root.$dialog.confirm({
          text: root.$t("contract_admin.delete_confirm_msg"),
        })
      ) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/contract/admin/delete/",
          params: { contract_admin_id: item.id },
        });

        if (response.body.success) {
          await refresh();
        }
      }
    };
    const approveItem = async function (item) {
      if (
        await root.$dialog.confirm({
          text: root.$t("contract_admin.approve_confirm_msg"),
        })
      ) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/contract/admin/approve/",
          params: { contract_admin_id: item.id },
        });

        if (response.body.success) {
          await refresh();
        }
      }
    };
    const declineItem = async function (item) {
      if (
        await root.$dialog.confirm({
          text: root.$t("contract_admin.decline_confirm_msg"),
        })
      ) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/contract/admin/decline/",
          params: { contract_admin_id: item.id },
        });

        if (response.body.success) {
          await refresh();
        }
      }
    };

    const canTransferItem = function (item) {
      return (
        [USER_TYPE.ADMIN, USER_TYPE.SUPER_ADMIN].indexOf(me.value.type) !==
          -1 || me.value.managed_contract_ids.indexOf(props.contractId) !== -1
      );
    };
    const transferItem = async function (item) {
      const account = await root.$dialog.showAndWait(SelectAccountDialog, {
        title: root.$t("contract_admin.transfer_title"),
        showClose: false,
        persistent: true,
        width: "640px",
      });

      if (
        account &&
        (await root.$dialog.confirm({
          text: root.$t("contract_admin.transfer_confirm_msg"),
        }))
      ) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/contract/admin/transfer/",
          params: {
            contract_admin_id: item.id,
            account_id: account.id,
          },
        });

        if (response.body.success) {
          await refresh();
        }
      }
    };

    return {
      filterOptions,

      headers,
      innerRefreshTrigger,
      selectedItemIds,
      totalItemCount,

      editItem,
      canDeleteItem,
      deleteItem,
      approveItem,
      declineItem,
      canTransferItem,
      transferItem,
    };
  },
};
</script>

<style lang="less">
.contract-admin-list {
  .admin-status-span {
    .admin-status-dot {
      height: 10px;
      width: 10px;
      border-radius: 10px;
      display: inline-block;
    }

    &.DECLINED {
      color: #ef3924;

      .admin-status-dot {
        background-color: #ef3924;
      }
    }

    &.PENDING {
      color: #d4a106;

      .admin-status-dot {
        background-color: #d4a106;
      }
    }

    &.APPROVED {
      color: #5bc23f;

      .admin-status-dot {
        background-color: #5bc23f;
      }
    }
  }
}
</style>
